<script lang="ts" setup>
import FilterTable from "@/components/Common/Table/FilterTable.vue";
import { ref } from "vue";
import type { Header } from "vue3-easy-data-table";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import Swal from "sweetalert2";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { useCascadingSchemeStore } from "@/stores/management/cascading-scheme";
import { hasAccess } from "@/utils/helper";

const linkAdd = {
  text: "Add Cascading Scheme",
  url: "/management/cascading-scheme/create",
};

const headers: Header[] = [
  { text: "CATEGORY", value: "group_category_name" },
  { text: "SUBMISSION", value: "submission_job_name" },
  { text: "APPROVAL", value: "approval_job_name" },
  { text: "STEP/LEVEL", value: "step_number" },
  { text: "ACTIONS", value: "action" },
];

const cascadingSchemeStore = useCascadingSchemeStore();
const page = ref<number>(1);
const limit = ref<number>(10);
const totalItems = ref<number>(0);
const listCascadingScheme = ref<any>([]);
const isLoading = ref(false);

const loadCascadingScheme = async (query = "") => {
  isLoading.value = true;
  const { success, message } = await cascadingSchemeStore.dispatchGetList(
    page.value,
    limit.value,
    query
  );
  if (success) {
    listCascadingScheme.value = await cascadingSchemeStore.schemes.items;
    page.value = await cascadingSchemeStore.schemes.page;
    limit.value = await cascadingSchemeStore.schemes.pageSize;
    totalItems.value = await cascadingSchemeStore.schemes.total;
  }
  isLoading.value = false;
};

const handleServerOptionsUpdate = (options: {
  page: number;
  rowsPerPage: number;
}) => {
  page.value = options.page;
  limit.value = options.rowsPerPage;
  loadCascadingScheme();
};

const handleEdit = (scheme: any) => {
  router.push("/management/cascading-scheme/" + scheme);
};

const handleDelete = async (scheme: any) => {
  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      cancelButton: "btn btn-danger",
      confirmButton: "btn btn-success",
    },
    buttonsStyling: false,
  });
  swalWithBootstrapButtons
    .fire({
      title: `Are you sure want to delete data ${scheme.name} ?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    })
    .then(async (result) => {
      if (result.isConfirmed) {
        const { success, message } = await cascadingSchemeStore.dispatchDelete(
          scheme.id
        );
        if (success) {
          swalWithBootstrapButtons.fire({
            title: "Deleted!",
            text: "Your file has been deleted.",
            icon: "success",
          });
          loadCascadingScheme();
        } else {
          toast.error(message);
        }
      } else if (
        /* Read more about handling dismissals below */
        result.dismiss === Swal.DismissReason.cancel
      ) {
        swalWithBootstrapButtons.fire({
          title: "Cancelled",
          text: "Your action has been aborted",
          icon: "error",
        });
      }
    });
};

loadCascadingScheme();
</script>
<template>
  <div class="card bg-white border-0 rounded-3 mb-4">
    <div class="card-body p-4">
      <div
        class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
      >
        <h3 class="mb-0">List Cascading Scheme</h3>
      </div>
      <FilterTable :link="linkAdd" @searchByQuery="loadCascadingScheme" />
      <div class="table-content">
        <BaseLoading v-if="isLoading" />
        <EasyDataTable
          :headers="headers"
          :items="listCascadingScheme"
          v-if="!isLoading"
          show-index
          :server-options="{
            page: page,
            rowsPerPage: limit,
          }"
          :server-items-length="totalItems"
          @update:server-options="handleServerOptionsUpdate"
          buttons-pagination
        >
          <template #item-image="props">
            <img :src="props.image_link" alt="image-link" class="image-item" />
          </template>
          <template #item-action="props">
            <div class="d-flex gap-2">
              <button
                v-if="hasAccess('cascading_scheme-update')"
                class="btn btn-info btn-sm text-white"
                @click="handleEdit(props.id)"
              >
                Edit
              </button>
              <button
                v-if="hasAccess('cascading_scheme-delete')"
                class="btn btn-danger btn-sm"
                @click="handleDelete(props)"
              >
                Delete
              </button>
            </div>
          </template>
        </EasyDataTable>
      </div>
    </div>
  </div>
</template>
<style>
.image-item {
  width: 200px;
  height: auto;
  object-fit: contain;
  padding: 10px;
}
</style>
